import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { registerQuery, useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { H3 } from 'UIComponents/elements/headings';
import UIFlex from 'UIComponents/layout/UIFlex';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UIClickable from 'UIComponents/button/UIClickable';
import Big from 'UIComponents/elements/Big';
import UIBadge from 'UIComponents/badge/UIBadge';
import { WEB_FONT_DEMI_BOLD_WEIGHT } from 'HubStyleTokens/misc';
/* @ts-expect-error module is untyped */

import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import { fetchUserInsights } from '../../clients/profilesClient';
/* @ts-expect-error module is untyped */

import { push } from '../../utils/history';
import { fromFilterStateToLocation } from '../../utils/storefrontFilters';
/* @ts-expect-error module is untyped */

import GenericError from '../error/GenericError';
const GET_USER_INSIGHTS = registerQuery({
  fieldName: 'insights',
  fetcher: () => fetchUserInsights()
});
const RECOMMENDATION_PRIORITY = {
  country: 'country',
  industry: 'industry',
  language: 'language'
};

const RecommendationCard = ({
  recommendationKey,
  recommendationValue,
  onClick
}) => {
  const {
    cardWidth
  } = useResponsiveProps({
    cardWidth: {
      lg: '300px',
      xs: '275px'
    }
  });
  return /*#__PURE__*/_jsx(UIClickable, {
    "data-test-id": `${recommendationKey}-recommendation-card`,
    onClick: onClick,
    children: /*#__PURE__*/_jsx(UICardWrapper, {
      className: "m-all-3",
      height: 200,
      style: {
        width: cardWidth
      },
      compact: true,
      children: /*#__PURE__*/_jsx(UICardSection, {
        style: {
          height: '100%'
        },
        children: /*#__PURE__*/_jsxs(UIFlex, {
          justify: "between",
          direction: "column",
          style: {
            height: '100%'
          },
          children: [/*#__PURE__*/_jsx(Big, {
            "data-test-id": `${recommendationKey}-card-title`,
            style: {
              minHeight: '50px',
              fontWeight: WEB_FONT_DEMI_BOLD_WEIGHT
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ecosystem-marketplace-solutions-ui.homepage.recommendations.card.${recommendationKey}.title`,
              options: {
                [recommendationKey]: recommendationValue
              }
            })
          }), /*#__PURE__*/_jsx(UIBadge, {
            use: "beta",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.homepage.recommendations.card.recommended"
            })
          }), /*#__PURE__*/_jsx("p", {
            className: "m-bottom-0",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ecosystem-marketplace-solutions-ui.homepage.recommendations.card.${recommendationKey}.subtitle`,
              options: {
                [recommendationKey]: recommendationValue
              }
            })
          })]
        })
      })
    })
  });
};

export default function HomepageRecommendations() {
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const {
    data: insightsData,
    loading: insightsLoading,
    error: insightsError
  } = useQuery(GET_USER_INSIGHTS);
  const {
    flexWrapperDirection
  } = useResponsiveProps({
    flexWrapperDirection: {
      lg: 'row',
      xs: 'column'
    }
  });
  const normalizedUserInsights = useMemo(() => {
    const insights = insightsData && insightsData.insights; // flatten user insights
    // and set country to ipCountry *only* if no hqCountry already exists

    return insights && Object.assign({
      country: insights.portalInsights && insights.portalInsights.hqCountry || insights.ipCountry
    }, insights.portalInsights);
  }, [insightsData]);
  const buildRecommendationTranslationKey = useCallback((recommendationKey, recommendationValue) => {
    let translationKey = '';

    switch (recommendationKey) {
      case RECOMMENDATION_PRIORITY.country:
        translationKey = `SharedI18nStrings.countryCodes.${recommendationValue}`;
        break;

      case RECOMMENDATION_PRIORITY.industry:
        translationKey = `marketplace-ui-common.industry.${recommendationValue}.sentence`;
        break;

      case RECOMMENDATION_PRIORITY.language:
        translationKey = `SharedI18nStrings.languageNames.${recommendationValue}`;
        break;

      default:
        break;
    }

    if (!isValidI18nKey(translationKey)) {
      Raven.captureMessage(`Invalid ${recommendationKey} translation - ${translationKey}`, {
        level: 'warning',
        extra: {
          value: recommendationValue
        }
      });
      return null;
    }

    return translationKey;
  }, []);
  const renderedRecommendationCards = useMemo(() => {
    return normalizedUserInsights && Object.keys(RECOMMENDATION_PRIORITY).reduce((renderedRecommendations, recommendationKey) => {
      const value = recommendationKey === RECOMMENDATION_PRIORITY.language ? getLang() : normalizedUserInsights[recommendationKey]; // if there's no value associated with the key,
      // there's no reason to calculate the rest - return null
      // filter it out later

      if (!value) {
        return renderedRecommendations;
      }

      const translationKey = value && buildRecommendationTranslationKey(recommendationKey, value);
      const parsedRecommendation = {
        recommendationKey,
        recommendationTranslatedValue: unescapedText(translationKey),
        recommendationValue: value
      };
      renderedRecommendations.push( /*#__PURE__*/_jsx(RecommendationCard, {
        onClick: () => {
          trackInteraction('click homepage recommendation', {
            feature: parsedRecommendation.recommendationKey,
            item_type: parsedRecommendation.recommendationValue
          });
          push(fromFilterStateToLocation({
            [parsedRecommendation.recommendationKey]: [parsedRecommendation.recommendationValue]
          }, []));
        },
        recommendationKey: parsedRecommendation.recommendationKey,
        recommendationValue: parsedRecommendation.recommendationTranslatedValue
      }, `${parsedRecommendation.recommendationKey}-recommendation`));
      return renderedRecommendations;
    }, []);
  }, [normalizedUserInsights, buildRecommendationTranslationKey, trackInteraction]);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "recommended-for-you",
    className: "p-bottom-8",
    children: [/*#__PURE__*/_jsx(H3, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.recommendations.title"
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.recommendations.subtitle"
      })
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: getDataFetchingClientRequestState(insightsError, insightsLoading),
      ErrorComponent: GenericError,
      children: /*#__PURE__*/_jsx(UIFlex, {
        direction: flexWrapperDirection,
        children: renderedRecommendationCards
      })
    })]
  });
}